import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Grid,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputLeftElement,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Skeleton,
    Stack,
    Stat,
    StatLabel,
    StatNumber,
    Switch,
    Text,
    Textarea,
    Tooltip,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {FaSearch} from "react-icons/fa";
import {MdAdd, MdCampaign, MdDelete, MdEdit, MdLink, MdOpenInNew} from "react-icons/md";
import {deleteAdvertisement, saveAdvertisement, searchAdvertisements} from "./Services";

// The search endpoint returns { advertisementList, size }; be tolerant of a few
// alternate shapes so the page never blanks out on a minor backend change.
const extractList = (data) => {
    if (Array.isArray(data?.advertisementList)) return data.advertisementList;
    if (Array.isArray(data?.data?.advertisementList)) return data.data.advertisementList;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.content)) return data.content;
    return [];
};

// status comes back as a single char: "Y" active, "N" inactive, "B" blocked.
const STATUS_META = {
    Y: {label: "Active", colorScheme: "green"},
    N: {label: "Inactive", colorScheme: "gray"},
    B: {label: "Blocked", colorScheme: "red"},
};
const statusMeta = (status) => STATUS_META[status] || {label: status || "—", colorScheme: "gray"};

const EMPTY_FORM = {id: null, title: "", description: "", url: "", enabled: true};

/* ----------------------- add / edit modal ----------------------- */

function AdvertisementModal({isOpen, onClose, onSaved, editing}) {
    const toast = useToast();
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [imgBroken, setImgBroken] = useState(false);

    const previewBg = useColorModeValue("gray.50", "whiteAlpha.100");

    // Seed the form whenever the modal opens (edit → prefill, create → blank).
    useEffect(() => {
        if (isOpen) {
            setForm(editing ? {...EMPTY_FORM, ...editing} : EMPTY_FORM);
            setImgBroken(false);
        }
    }, [isOpen, editing]);

    const setField = (key) => (e) => {
        const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((prev) => ({...prev, [key]: value}));
        if (key === "url") setImgBroken(false);
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            toast({title: "Title is required", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        setSaving(true);
        try {
            const request = {
                // Only include id when editing so the upsert edits vs. creates.
                ...(form.id ? {id: form.id} : {}),
                title: form.title.trim(),
                description: form.description?.trim() || null,
                url: form.url?.trim() || null,
                enabled: Boolean(form.enabled),
            };
            const response = await saveAdvertisement(request);
            if (response.status === 201 || response.status === 200) {
                toast({
                    title: form.id ? "Advertisement updated" : "Advertisement created",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                onSaved();
                onClose();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({
                title: "Could not save",
                description: e?.errorMessage || "Saving the advertisement failed.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const showPreview = form.url?.trim() && !imgBroken;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>{form.id ? "Edit Advertisement" : "New Advertisement"}</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl isRequired>
                            <FormLabel>Title</FormLabel>
                            <Input
                                value={form.title}
                                onChange={setField("title")}
                                placeholder="e.g. Summer Sale"
                                autoFocus
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                value={form.description || ""}
                                onChange={setField("description")}
                                placeholder="Short blurb shown under the title in the app carousel"
                                rows={3}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>Image / link URL</FormLabel>
                            <InputGroup>
                                <InputLeftElement pointerEvents="none" children={<Icon as={MdLink} color="gray.400"/>}/>
                                <Input
                                    value={form.url || ""}
                                    onChange={setField("url")}
                                    placeholder="https://cdn.example.com/ads/summer.png"
                                />
                            </InputGroup>
                            <FormHelperText>The image shown in the app advertisement carousel.</FormHelperText>
                        </FormControl>

                        {showPreview && (
                            <Box borderRadius="12px" overflow="hidden" bg={previewBg} borderWidth="1px" borderColor="gray.100" _dark={{borderColor: "gray.700"}}>
                                <Image
                                    src={form.url.trim()}
                                    alt="Advertisement preview"
                                    w="100%"
                                    h="160px"
                                    objectFit="cover"
                                    onError={() => setImgBroken(true)}
                                />
                            </Box>
                        )}

                        <FormControl display="flex" alignItems="center">
                            <Switch
                                isChecked={Boolean(form.enabled)}
                                onChange={setField("enabled")}
                                mr={3}
                                colorScheme="teal"
                            />
                            <FormLabel mb={0}>{form.enabled ? "Enabled (visible to the app)" : "Disabled"}</FormLabel>
                        </FormControl>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="teal" onClick={handleSave} isLoading={saving} isDisabled={!form.title.trim()}>
                        {form.id ? "Save changes" : "Create"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* --------------------- delete confirm modal --------------------- */

function DeleteModal({isOpen, onClose, onConfirm, target, deleting}) {
    const [remarks, setRemarks] = useState("");

    useEffect(() => {
        if (isOpen) setRemarks("");
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <ModalOverlay/>
            <ModalContent>
                <ModalHeader>Delete advertisement</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Text mb={3}>
                        Are you sure you want to delete <b>{target?.title || "this advertisement"}</b>? It will be
                        removed from the app.
                    </Text>
                    <FormControl>
                        <FormLabel>Remarks (optional)</FormLabel>
                        <Input
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            placeholder="e.g. Campaign ended"
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="red" onClick={() => onConfirm(remarks)} isLoading={deleting}>Delete</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

/* ----------------------------- card ----------------------------- */

function AdCard({ad, onEdit, onDelete, onToggle, toggling}) {
    const cardBg = useColorModeValue("white", "gray.700");
    const meta = statusMeta(ad.status);
    const [imgBroken, setImgBroken] = useState(false);

    return (
        <Box
            bg={cardBg}
            borderRadius="16px"
            boxShadow="sm"
            border="1px solid"
            borderColor={ad.enabled ? "teal.200" : "gray.100"}
            overflow="hidden"
            transition="all 0.2s ease"
            _hover={{transform: "translateY(-4px)", boxShadow: "lg"}}
            _dark={{borderColor: ad.enabled ? "teal.700" : "gray.600"}}
        >
            {/* Image / placeholder */}
            <Box position="relative" h="150px" bg="gray.100" _dark={{bg: "gray.800"}}>
                {ad.url && !imgBroken ? (
                    <Image
                        src={ad.url}
                        alt={ad.title}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        onError={() => setImgBroken(true)}
                    />
                ) : (
                    <Flex align="center" justify="center" h="100%" color="gray.300">
                        <Icon as={MdCampaign} boxSize="48px"/>
                    </Flex>
                )}
                <HStack position="absolute" top={2} right={2} spacing={2}>
                    <Badge colorScheme={meta.colorScheme} borderRadius="full" px={2}>{meta.label}</Badge>
                    <Badge
                        colorScheme={ad.enabled ? "teal" : "gray"}
                        variant="solid"
                        borderRadius="full"
                        px={2}
                    >
                        {ad.enabled ? "Enabled" : "Disabled"}
                    </Badge>
                </HStack>
            </Box>

            {/* Body */}
            <Box p={4}>
                <Text fontWeight="bold" noOfLines={1}>{ad.title || "Untitled"}</Text>
                {ad.description && (
                    <Text fontSize="sm" color="gray.500" noOfLines={2} mt={1}>{ad.description}</Text>
                )}
                {ad.url && (
                    <Link href={ad.url} isExternal fontSize="xs" color="teal.500" mt={2} display="inline-flex" alignItems="center" gap={1} noOfLines={1}>
                        <Icon as={MdOpenInNew} boxSize={3}/> Open link
                    </Link>
                )}

                <Flex align="center" justify="space-between" mt={4}>
                    <HStack spacing={2}>
                        <Tooltip label={ad.enabled ? "Disable" : "Enable"} hasArrow>
                            <Box>
                                <Switch
                                    isChecked={Boolean(ad.enabled)}
                                    onChange={() => onToggle(ad)}
                                    colorScheme="teal"
                                    isDisabled={toggling}
                                />
                            </Box>
                        </Tooltip>
                        <Text fontSize="xs" color="gray.400">#{ad.id}</Text>
                    </HStack>
                    <HStack spacing={1}>
                        <Tooltip label="Edit" hasArrow>
                            <IconButton aria-label="Edit advertisement" icon={<MdEdit/>} size="sm" variant="ghost" colorScheme="blue" onClick={() => onEdit(ad)}/>
                        </Tooltip>
                        <Tooltip label="Delete" hasArrow>
                            <IconButton aria-label="Delete advertisement" icon={<MdDelete/>} size="sm" variant="ghost" colorScheme="red" onClick={() => onDelete(ad)}/>
                        </Tooltip>
                    </HStack>
                </Flex>
            </Box>
        </Box>
    );
}

/* ----------------------------- page ----------------------------- */

function Advertisement() {
    const toast = useToast();
    const editModal = useDisclosure();
    const deleteModal = useDisclosure();
    const cardBg = useColorModeValue("white", "gray.700");
    const pageBg = useColorModeValue("gray.50", "gray.900");

    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [editing, setEditing] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    const fetchAds = useCallback(async () => {
        setLoading(true);
        try {
            const response = await searchAdvertisements();
            if (response.status === 200) {
                setAds(extractList(response.data));
            }
        } catch (e) {
            toast({
                title: "Could not load advertisements",
                description: "Failed to fetch the advertisement list.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        fetchAds();
    }, [fetchAds]);

    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return ads;
        return ads.filter(
            (ad) =>
                String(ad.title || "").toLowerCase().includes(term) ||
                String(ad.description || "").toLowerCase().includes(term)
        );
    }, [ads, searchTerm]);

    const stats = useMemo(() => {
        const enabled = ads.filter((ad) => Boolean(ad.enabled)).length;
        const active = ads.filter((ad) => ad.status === "Y").length;
        return {total: ads.length, enabled, active};
    }, [ads]);

    const openCreate = () => {
        setEditing(null);
        editModal.onOpen();
    };

    const openEdit = (ad) => {
        setEditing(ad);
        editModal.onOpen();
    };

    const openDelete = (ad) => {
        setDeleteTarget(ad);
        deleteModal.onOpen();
    };

    const handleDeleteConfirm = async (remarks) => {
        if (!deleteTarget?.id) return;
        setDeleting(true);
        try {
            const response = await deleteAdvertisement({id: deleteTarget.id, remarks: remarks || null});
            if (response.status === 200 || response.status === 201) {
                toast({title: "Advertisement deleted", status: "info", duration: 2500, isClosable: true});
                deleteModal.onClose();
                setDeleteTarget(null);
                fetchAds();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({
                title: "Delete failed",
                description: e?.errorMessage || "Could not delete this advertisement.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setDeleting(false);
        }
    };

    // Quick enable/disable — re-save (upsert) the ad with the flag flipped.
    const handleToggle = async (ad) => {
        setTogglingId(ad.id);
        try {
            const response = await saveAdvertisement({
                id: ad.id,
                title: ad.title,
                description: ad.description || null,
                url: ad.url || null,
                enabled: !ad.enabled,
            });
            if (response.status === 201 || response.status === 200) {
                // Optimistic local flip, then refresh from the server.
                setAds((prev) => prev.map((item) => (item.id === ad.id ? {...item, enabled: !ad.enabled} : item)));
                toast({
                    title: !ad.enabled ? "Advertisement enabled" : "Advertisement disabled",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                });
                fetchAds();
            } else {
                throw new Error("Unexpected status");
            }
        } catch (e) {
            toast({title: "Could not update", status: "error", duration: 3000, isClosable: true});
        } finally {
            setTogglingId(null);
        }
    };

    return (
        <Flex direction="column" pt={{base: "110px", md: "80px"}} px={{base: "4", md: "8"}} pb="80px" bg={pageBg} minH="100vh">
            {/* Header */}
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap={4} mb={6}>
                <HStack spacing={3}>
                    <Flex w="46px" h="46px" borderRadius="12px" bgGradient="linear(to-br, orange.400, pink.500)" color="white" align="center" justify="center">
                        <Icon as={MdCampaign} boxSize="28px"/>
                    </Flex>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">Advertisements</Text>
                        <Text color="gray.500">Manage the promotions shown in the app / client carousel.</Text>
                    </Box>
                </HStack>
                <Button colorScheme="teal" leftIcon={<MdAdd/>} onClick={openCreate}>Add Advertisement</Button>
            </Flex>

            {/* Stats */}
            <SimpleGrid columns={{base: 1, sm: 3}} spacing={4} mb={6}>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Total</StatLabel>
                    <StatNumber>{stats.total}</StatNumber>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Enabled</StatLabel>
                    <StatNumber>{stats.enabled}</StatNumber>
                </Stat>
                <Stat bg={cardBg} p={4} borderRadius="12px" boxShadow="sm">
                    <StatLabel color="gray.500">Active</StatLabel>
                    <StatNumber>{stats.active}</StatNumber>
                </Stat>
            </SimpleGrid>

            {/* Search */}
            <Box mb={6} maxW="480px">
                <InputGroup boxShadow="sm" borderRadius="md" bg={cardBg}>
                    <InputLeftElement pointerEvents="none" children={<FaSearch color="gray"/>}/>
                    <Input
                        placeholder="Search advertisements by title or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        borderRadius="md"
                    />
                </InputGroup>
            </Box>

            {/* List */}
            {loading ? (
                <Grid templateColumns={{base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr"}} gap={5}>
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} height="290px" borderRadius="16px"/>
                    ))}
                </Grid>
            ) : filtered.length === 0 ? (
                <Flex direction="column" align="center" justify="center" bg={cardBg} borderRadius="16px" boxShadow="sm" py="80px" textAlign="center">
                    <Box color="orange.400" fontSize="56px" mb={4}><MdCampaign/></Box>
                    <Text fontSize="lg" fontWeight="bold">{searchTerm ? "No matches" : "No advertisements yet"}</Text>
                    <Text color="gray.500" mt={1}>
                        {searchTerm ? "Try a different search term." : "Click “Add Advertisement” to create your first one."}
                    </Text>
                </Flex>
            ) : (
                <Grid templateColumns={{base: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr"}} gap={5}>
                    {filtered.map((ad) => (
                        <AdCard
                            key={ad.id}
                            ad={ad}
                            onEdit={openEdit}
                            onDelete={openDelete}
                            onToggle={handleToggle}
                            toggling={togglingId === ad.id}
                        />
                    ))}
                </Grid>
            )}

            <AdvertisementModal
                isOpen={editModal.isOpen}
                onClose={editModal.onClose}
                onSaved={fetchAds}
                editing={editing}
            />
            <DeleteModal
                isOpen={deleteModal.isOpen}
                onClose={deleteModal.onClose}
                onConfirm={handleDeleteConfirm}
                target={deleteTarget}
                deleting={deleting}
            />
        </Flex>
    );
}

export default Advertisement;
